import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Plus,
  Building2,
  Trash2,
  Users,
  Edit,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCompanies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    siret: "",
    license_count: 5,
    is_active: true,
    manager_email: "",
    manager_first_name: "",
    manager_last_name: "",
    send_credentials_email: true,
    privacy_accepted: false,
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies`);
      setCompanies(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      phone: "",
      email: "",
      siret: "",
      license_count: 5,
      is_active: true,
      manager_email: "",
      manager_first_name: "",
      manager_last_name: "",
      send_credentials_email: true,
    });
    setEditingCompany(null);
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name,
        address: company.address || "",
        phone: company.phone || "",
        email: company.email || "",
        siret: company.siret || "",
        license_count: company.license_count,
        is_active: company.is_active,
        manager_email: "",
        send_credentials_email: true,
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Le nom est obligatoire");
      return;
    }
    
    if (!editingCompany && !formData.manager_email && !formData.email) {
      toast.error("L'email du client est obligatoire");
      return;
    }

    if (!editingCompany && (!formData.manager_first_name || !formData.manager_last_name)) {
      toast.error("Le nom et prénom du gérant sont obligatoires");
      return;
    }

    if (!editingCompany && !formData.privacy_accepted) {
      toast.error("Vous devez accepter la politique de confidentialité");
      return;
    }

    setIsCreating(true);
    try {
      if (editingCompany) {
        await axios.put(`${API}/admin/companies/${editingCompany.id}`, formData);
        toast.success("Entreprise mise à jour");
        setShowDialog(false);
      } else {
        // Create company with manager - email will be sent automatically
        const response = await axios.post(`${API}/admin/companies`, {
          ...formData,
          manager_email: formData.manager_email || formData.email,
        });
        
        setShowDialog(false);
        
        if (response.data.email_sent) {
          toast.success(`Compte créé avec succès. Un email a été envoyé à ${response.data.manager.email} pour définir son mot de passe.`, { duration: 6000 });
        } else {
          toast.warning(`Entreprise créée mais email non envoyé: ${response.data.email_error || 'Erreur inconnue'}. L'utilisateur devra utiliser "Mot de passe oublié" pour configurer son accès.`, { duration: 8000 });
        }
      }
      resetForm();
      fetchCompanies();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (companyId) => {
    try {
      await axios.delete(`${API}/admin/companies/${companyId}`);
      toast.success("Entreprise supprimée");
      fetchCompanies();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="admin-companies">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/admin")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold text-slate-900">Entreprises</h1>
            </div>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-sky-600 hover:bg-sky-700 h-12"
              data-testid="add-company-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvelle entreprise
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : companies.length > 0 ? (
          <div className="space-y-4">
            {companies.map((company) => (
              <Card key={company.id} data-testid={`company-card-${company.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          company.is_active ? "bg-sky-100" : "bg-slate-100"
                        }`}
                      >
                        <Building2
                          className={`w-7 h-7 ${
                            company.is_active ? "text-sky-600" : "text-slate-400"
                          }`}
                        />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {company.name}
                          </h3>
                          <span
                            className={`px-2 py-0.5 text-xs rounded ${
                              company.is_active
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {company.is_active ? "Actif" : "Inactif"}
                          </span>
                        </div>
                        {company.address && (
                          <p className="text-sm text-slate-500 mt-1">
                            {company.address}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Users className="w-4 h-4" />
                            <span>
                              {company.user_count}/{company.license_count} utilisateurs
                            </span>
                          </div>
                          {company.siret && (
                            <span className="text-sm text-slate-400">
                              SIRET: {company.siret}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          Créé le{" "}
                          {format(new Date(company.created_at), "dd/MM/yyyy", {
                            locale: fr,
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleOpenDialog(company)}
                        data-testid={`edit-company-${company.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Supprimer cette entreprise ?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action supprimera l'entreprise "{company.name}"
                              et toutes ses données associées.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(company.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 text-lg mb-2">Aucune entreprise</p>
            <p className="text-slate-400 text-sm mb-6">
              Créez votre première entreprise cliente
            </p>
          </div>
        )}
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCompany ? "Modifier l'entreprise" : "Nouvelle entreprise"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom de l'entreprise *</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Ex: Plomberie Dupont"
                className="h-12 mt-1"
                data-testid="input-company-name"
              />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, address: e.target.value }))
                }
                className="h-12 mt-1"
                data-testid="input-company-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Téléphone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  className="h-12 mt-1"
                  data-testid="input-company-phone"
                />
              </div>
              <div>
                <Label>SIRET</Label>
                <Input
                  value={formData.siret}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, siret: e.target.value }))
                  }
                  className="h-12 mt-1"
                  data-testid="input-company-siret"
                />
              </div>
            </div>
            
            {!editingCompany && (
              <>
                <div className="pt-4 border-t">
                  <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-sky-600" />
                    Compte Manager
                  </h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Nom du gérant *</Label>
                        <Input
                          value={formData.manager_last_name}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, manager_last_name: e.target.value }))
                          }
                          placeholder="Dupont"
                          className="h-12 mt-1"
                          data-testid="input-manager-last-name"
                        />
                      </div>
                      <div>
                        <Label>Prénom du gérant *</Label>
                        <Input
                          value={formData.manager_first_name}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, manager_first_name: e.target.value }))
                          }
                          placeholder="Jean"
                          className="h-12 mt-1"
                          data-testid="input-manager-first-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Email du manager *</Label>
                      <Input
                        type="email"
                        value={formData.manager_email || formData.email}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, manager_email: e.target.value, email: e.target.value }))
                        }
                        placeholder="manager@entreprise.fr"
                        className="h-12 mt-1"
                        data-testid="input-manager-email"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Un compte manager sera créé avec cet email
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 bg-sky-50 p-4 rounded-lg">
                  <Checkbox
                    id="send_email"
                    checked={formData.send_credentials_email}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, send_credentials_email: checked }))
                    }
                    data-testid="checkbox-send-email"
                  />
                  <div>
                    <label htmlFor="send_email" className="text-sm font-medium text-slate-700 cursor-pointer">
                      Envoyer l'email d'activation du compte
                    </label>
                    <p className="text-xs text-slate-500">
                      Le client recevra un email avec un lien pour définir son mot de passe
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <Checkbox
                    id="privacy_accepted"
                    checked={formData.privacy_accepted}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, privacy_accepted: checked }))
                    }
                    className="mt-0.5"
                    data-testid="checkbox-privacy-accepted"
                  />
                  <div>
                    <label htmlFor="privacy_accepted" className="text-sm font-medium text-slate-700 cursor-pointer">
                      J'accepte la{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-600 hover:underline"
                        data-testid="privacy-policy-link"
                      >
                        politique de confidentialité
                      </a>
                      {" "}*
                    </label>
                  </div>
                </div>
              </>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre de licences</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.license_count}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      license_count: parseInt(e.target.value) || 1,
                    }))
                  }
                  className="h-12 mt-1"
                  data-testid="input-company-licenses"
                />
              </div>
              <div className="flex items-center justify-between pt-6">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, is_active: checked }))
                  }
                  data-testid="switch-company-active"
                />
              </div>
            </div>
            
            <Button
              onClick={handleSave}
              disabled={isCreating}
              className="w-full h-12 bg-sky-600 hover:bg-sky-700"
              data-testid="save-company-btn"
            >
              {isCreating ? (
                <div className="spinner w-5 h-5 border-white border-t-transparent" />
              ) : editingCompany ? (
                "Enregistrer"
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Créer et envoyer les identifiants
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog - removed, replaced by toast notifications */}
    </div>
  );
};

export default AdminCompanies;
